import { React, connect, fetchApi, NavLink } from '@gasbuddy/react';
import {
  Dimmer,
  Loader,
  Segment,
  Table,
  Button,
  Checkbox,
  Message,
  Icon,
} from 'semantic-ui-react';
import { Auth, Nav } from '../../components';
import { Role } from '../../roles';

function formatSentTimestamp(isoString) {
  // fleet-serv sends an ISO-8601 string, or '' when SQS omitted SentTimestamp;
  // displayed verbatim in ISO format
  if (!isoString) return '-';
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? '-' : date.toISOString();
}

class BaseUnprocessableBilling extends React.Component {
  state = {
    loading: false,
    groups: null,
    listedCount: 0,
    truncated: false,
    approximateTotal: null,
    // selected: { [messageId]: { messageId, transactionId, errorDetail } }
    selected: {},
    results: null,
    replaying: false,
    error: null,
  }

  loadMessages = () => {
    this.setState({ loading: true, groups: null, selected: {}, results: null, error: null }, async () => {
      try {
        const { body, status } = await fetchApi({
          url: '/payments/unprocessable-billing/messages',
          httpResponseErrors: true,
        });
        if (status < 200 || status > 299) {
          this.setState({ loading: false, error: (body && body.message) || 'Failed to load messages' });
          return;
        }
        this.setState({
          loading: false,
          groups: body.groups || [],
          listedCount: body.listedCount || 0,
          truncated: !!body.truncated,
          approximateTotal: typeof body.approximateTotal === 'number' ? body.approximateTotal : null,
        });
      } catch (err) {
        this.setState({ loading: false, error: (err && err.message) || 'Failed to load messages' });
      }
    });
  }

  toggleSelect = (msg) => {
    const { selected } = this.state;
    const next = Object.assign({}, selected);
    if (next[msg.messageId]) {
      delete next[msg.messageId];
    } else {
      next[msg.messageId] = {
        messageId: msg.messageId,
        accountId: msg.accountId,
        transactionId: msg.transactionId,
        errorDetail: msg.errorDetail,
      };
    }
    this.setState({ selected: next });
  }

  toggleSelectGroup = (groupMessages) => {
    const { selected } = this.state;
    const next = Object.assign({}, selected);
    const allSelected = groupMessages.every(m => next[m.messageId]);
    if (allSelected) {
      groupMessages.forEach((m) => { delete next[m.messageId]; });
    } else {
      groupMessages.forEach((m) => {
        next[m.messageId] = {
          messageId: m.messageId,
          accountId: m.accountId,
          transactionId: m.transactionId,
          errorDetail: m.errorDetail,
        };
      });
    }
    this.setState({ selected: next });
  }

  replay = () => {
    const { selected } = this.state;
    const messages = Object.values(selected);
    if (!messages.length) return;
    this.setState({ replaying: true, results: null, error: null }, async () => {
      try {
        const { body, status } = await fetchApi({
          url: '/payments/unprocessable-billing/replay',
          body: { messages },
          httpResponseErrors: true,
        });
        if (status < 200 || status > 299) {
          this.setState({ replaying: false, error: (body && body.message) || 'Replay failed' });
          return;
        }
        this.setState({ replaying: false, results: body.results || [], selected: {} });
      } catch (err) {
        this.setState({ replaying: false, error: (err && err.message) || 'Replay failed' });
      }
    });
  }

  render() {
    const {
      loading, groups, selected, results, replaying, error,
      listedCount, truncated, approximateTotal,
    } = this.state;
    const moreThanListed = truncated || (approximateTotal != null && approximateTotal > listedCount);
    const selectedCount = Object.keys(selected).length;
    const busy = loading || replaying;

    return (
      <Auth forRoles={[Role.Superuser, Role.PaymentView]}>
        <Nav>
          <Dimmer.Dimmable as={Segment} dimmed={busy}>
            <Dimmer active={busy}>
              <Loader />
            </Dimmer>

            <h1>Unprocessable Billing Messages</h1>
            <p>
              Messages that failed permanent processing and were routed to
              {' '}
              <code>payment_fleetcor_billing_file_unprocessable</code>
              {'. This queue has a retention of approximately '}
              <strong>4 days</strong>
              {' - act promptly to avoid silent expiry.'}
            </p>
            <p>
              Replaying briefly hides the other messages in the batch for up to 30 seconds (SQS visibility timeout); reload afterwards if the list looks short.
              The DLQ is a standard (non-FIFO) queue and may occasionally show duplicate entries -
              replay deduplication is handled by the FIFO billing queue.
            </p>

            <Button onClick={this.loadMessages} disabled={busy}>
              <Icon name="refresh" />
              Load Messages
            </Button>

            {error && (
              <Message negative style={{ marginTop: 14 }}>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            {results && (
              <Message
                positive={results.every(r => r.status === 'replayed')}
                warning={results.some(r => r.status !== 'replayed')}
                style={{ marginTop: 14 }}
              >
                <Message.Header>Replay Results</Message.Header>
                {/* Not Message.List: its CSS bullet (content: '\u2022') renders as mojibake
                    because the stylesheet is served without a charset declaration */}
                {results.map(r => (
                  <p key={r.messageId} style={{ margin: '4px 0 0' }}>
                    <strong>{r.messageId}</strong>
                    {': '}
                    {r.status}
                    {r.error ? ` - ${r.error}` : ''}
                  </p>
                ))}
              </Message>
            )}

            {groups !== null && moreThanListed && (
              <Message warning style={{ marginTop: 14 }}>
                <Message.Header>Partial listing</Message.Header>
                <p>
                  {`Showing ${listedCount}${approximateTotal != null ? ` of ~${approximateTotal}` : ''} messages. `}
                  Replay or clear the listed messages and reload to fetch the rest.
                </p>
              </Message>
            )}

            {groups !== null && groups.length === 0 && (
              <Message info style={{ marginTop: 14 }}>
                <p>No unprocessable billing messages found in the queue.</p>
              </Message>
            )}

            {groups && groups.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
                  <Button
                    primary
                    onClick={this.replay}
                    disabled={!selectedCount || busy}
                    style={{ marginBottom: 14 }}
                  >
                    <Icon name="redo" />
                    {`Replay ${selectedCount ? `${selectedCount} ` : ''}Selected`}
                  </Button>
                </Auth>

                {groups.map((group) => {
                  const groupLabel = group.accountId != null
                    ? (
                      <span>
                        <NavLink
                          to={`/identity/users/${group.accountId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`Account ${group.accountId}`}
                        </NavLink>
                        {` (Employee# ${group.employeeNumber})`}
                      </span>
                    )
                    : `Employee# ${group.employeeNumber} (account decode failed)`;
                  const groupKey = group.accountId != null
                    ? `acct-${group.accountId}`
                    : `emp-${group.employeeNumber}`;
                  const allGroupSelected = group.messages.every(m => !!selected[m.messageId]);

                  return (
                    <Segment key={groupKey} style={{ marginBottom: 14 }}>
                      <h3>{groupLabel}</h3>
                      <Table compact celled>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell collapsing>
                              <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
                                <Checkbox
                                  checked={allGroupSelected}
                                  onChange={() => this.toggleSelectGroup(group.messages)}
                                  title="Select / deselect all in group"
                                />
                              </Auth>
                            </Table.HeaderCell>
                            <Table.HeaderCell>Message ID</Table.HeaderCell>
                            <Table.HeaderCell>Transaction ID</Table.HeaderCell>
                            <Table.HeaderCell>Employee#</Table.HeaderCell>
                            <Table.HeaderCell>Arrived (UTC)</Table.HeaderCell>
                            <Table.HeaderCell>Correlation ID</Table.HeaderCell>
                            <Table.HeaderCell>Error Detail</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {group.messages.map(msg => (
                            <Table.Row key={msg.messageId} warning={!!selected[msg.messageId]}>
                              <Table.Cell collapsing>
                                <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
                                  <Checkbox
                                    checked={!!selected[msg.messageId]}
                                    onChange={() => this.toggleSelect(msg)}
                                  />
                                </Auth>
                              </Table.Cell>
                              <Table.Cell>
                                <small>{msg.messageId}</small>
                              </Table.Cell>
                              <Table.Cell>{msg.transactionId}</Table.Cell>
                              <Table.Cell>{msg.employeeNumber}</Table.Cell>
                              <Table.Cell>{formatSentTimestamp(msg.sentTimestamp)}</Table.Cell>
                              <Table.Cell>
                                <small>{msg.correlationId}</small>
                              </Table.Cell>
                              <Table.Cell style={{ maxWidth: 320, wordBreak: 'break-word' }}>
                                {msg.errorDetail}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </Segment>
                  );
                })}
              </div>
            )}
          </Dimmer.Dimmable>
        </Nav>
      </Auth>
    );
  }
}

BaseUnprocessableBilling.propTypes = {
  // connected component; no props required
};

export const UnprocessableBilling = connect()(BaseUnprocessableBilling);
